import React, { useState, useEffect } from 'react'
import { Bot, Sparkles, Lock } from 'lucide-react'
import AIModal from './AIModal'
import { aiService } from '../../services/ai/AIService'
import { LessonStatsService } from '../../services/stats/LessonStatsService'
import { useAuth } from '../../contexts/AuthContext'
import { isProUser } from '../../utils/proUtils'
import aiUsageService from '../../services/ai/AIUsageService'
import { AIUsageStatus } from '../../types/ai'

interface AIButtonProps {
  content: any
  contentType: 'theory' | 'quiz' | 'formula' | 'exercise'
  context?: string
  title?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'primary' | 'secondary' | 'outline'
  questionId?: string
  lessonId?: string
  useDatabase?: boolean
}

const AIButton: React.FC<AIButtonProps> = ({
  content,
  contentType,
  context,
  title,
  className = '',
  size = 'md',
  variant = 'primary',
  questionId,
  lessonId,
  useDatabase = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [extractedContent, setExtractedContent] = useState('')
  const [usageStatus, setUsageStatus] = useState<AIUsageStatus | null>(null)
  const { user } = useAuth()
  
  const isPro = isProUser(user)

  useEffect(() => {
    if (user) {
        aiUsageService.getStatus(user.id).then(setUsageStatus).catch(() => {})
    }
  }, [user?.id, isModalOpen])

  const hasReachedLimit = usageStatus && usageStatus.remaining <= 0;
  // Học sinh không bị khoá theo lượt/ngày nữa — server tính lượt theo Gói AI học tập.
  const shouldShowLock = !isPro && user && user.role !== 'student' && hasReachedLimit;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // 📊 Track AI question usage
    LessonStatsService.recordAIQuestion(lessonId)
    
    // Trích xuất nội dung từ content object
    const contentString = aiService.extractContentForAI(content, contentType)
    setExtractedContent(contentString)
    setIsModalOpen(true)
  }

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm'
      case 'lg':
        return 'px-6 py-3 text-lg'
      default:
        return 'px-4 py-2 text-base'
    }
  }

  const getVariantClasses = () => {
    if (shouldShowLock) {
        return 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
    }

    switch (variant) {
      case 'secondary':
        return 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
      case 'outline':
        return 'bg-transparent text-blue-600 hover:bg-blue-50 border border-blue-300'
      default:
        return 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
    }
  }

  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 'w-4 h-4'
      case 'lg':
        return 'w-6 h-6'
      default:
        return 'w-5 h-5'
    }
  }

  const getContentTypeTitle = () => {
    if (title) return title
    
    switch (contentType) {
      case 'theory':
        return 'Giải thích lý thuyết'
      case 'quiz':
        return 'Giải thích câu hỏi'
      case 'formula':
        return 'Giải thích công thức'
      case 'exercise':
        return 'Giải thích bài tập'
      default:
        return 'Hỏi AI'
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center space-x-2 rounded-lg font-medium transition-all duration-200
          ${getSizeClasses()}
          ${getVariantClasses()}
          ${className}
        `}
        title={getContentTypeTitle()}
      >
        {shouldShowLock ? <Lock className={getIconSize()} /> : <Bot className={getIconSize()} />}
        <span>Hỏi AI</span>
        {!shouldShowLock && <Sparkles className={`${getIconSize()} opacity-70`} />}
      </button>

      <AIModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        content={extractedContent}
        contentType={contentType}
        context={context}
        title={getContentTypeTitle()}
        questionId={questionId}
        lessonId={lessonId}
        useDatabase={useDatabase}
      />
    </>
  )
}

export default AIButton
